import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AirlineDialog } from '@/components/airlines/AirlineDialog';
import { ViewToggle } from '@/components/shared/ViewToggle';
import { AirlineCard } from '@/components/airlines/AirlineCard';
interface Airline {
  id: string;
  name_ar: string;
  name_en: string;
  code: string;
  created_at: string;
  updated_at: string;
}
export default function Airlines() {
  const [airlines, setAirlines] = useState<Airline[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAirline, setEditingAirline] = useState<Airline | null>(null);
  const [view, setView] = useState<'table' | 'cards'>('table');
  const {
    toast
  } = useToast();
  const fetchAirlines = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('airlines').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setAirlines(data || []);
    } catch (error) {
      console.error('Error fetching airlines:', error);
      toast({
        title: "خطأ",
        description: "فشل في تحميل شركات الطيران",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (!confirm('هل أنت متأكد من حذف هذه الشركة؟')) return;
    try {
      const {
        error
      } = await supabase.from('airlines').delete().eq('id', id);
      if (error) throw error;
      toast({
        title: "تم الحذف",
        description: "تم حذف شركة الطيران بنجاح"
      });
      fetchAirlines();
    } catch (error) {
      console.error('Error deleting airline:', error);
      toast({
        title: "خطأ",
        description: "فشل في حذف شركة الطيران",
        variant: "destructive"
      });
    }
  };
  const handleEdit = (airline: Airline) => {
    setEditingAirline(airline);
    setDialogOpen(true);
  };
  const handleAdd = () => {
    setEditingAirline(null);
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingAirline(null);
    fetchAirlines();
  };
  useEffect(() => {
    fetchAirlines();
  }, []);
  if (loading) {
    return <div className="flex items-center justify-center h-96">
        <div className="text-lg">جاري التحميل...</div>
      </div>;
  }
  return <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">شركات الطيران</h1>
        
        <ViewToggle view={view} onViewChange={setView} />
        
        <Button onClick={handleAdd} className="gap-2">
          <Plus className="h-4 w-4" />
          إضافة شركة طيران
        </Button>
      </div>
      
      {view === 'table' ? <Card>
          
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">الاسم بالعربية</TableHead>
                  <TableHead className="text-right">الاسم بالإنجليزية</TableHead>
                  <TableHead className="text-right">الرمز</TableHead>
                  <TableHead className="text-right">تاريخ الإنشاء</TableHead>
                  <TableHead className="text-right">العمليات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {airlines.map(airline => <TableRow key={airline.id}>
                    <TableCell className="font-medium">{airline.name_ar}</TableCell>
                    <TableCell>{airline.name_en}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{airline.code}</Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(airline.created_at).toLocaleDateString('ar-SA')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(airline)}>
                          تعديل
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(airline.id)}>
                          حذف
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </CardContent>
        </Card> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {airlines.map(airline => <AirlineCard key={airline.id} airline={airline} onEdit={() => handleEdit(airline)} onDelete={() => handleDelete(airline.id)} />)}
        </div>}

      {airlines.length === 0 && !loading && <div className="text-center py-12 text-gray-500">
            <p>لم يتم العثور على أي شركات طيران.</p>
        </div>}

      <AirlineDialog open={dialogOpen} onOpenChange={setDialogOpen} airline={editingAirline} onClose={handleDialogClose} />
    </div>;
}